/**
 * Hook para buscar e gerenciar dados de unidades
 */
import { useQuery } from '@tanstack/react-query';
import { useUnidadesFilters } from './useUnidadesFilters';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api`;

// Tipos para respostas da API
export interface Unit {
  UnitId: number;
  UnitName: string;
  UnitGuid: string;
  Identification?: string;
  SituationName: string | null;
  UnitSituationName?: string | null;
  StatusUnitName: string | null;
  RegionalName: string | null;
  CityStateName: string | null;
  Cnpj: string | null;
  RegisteredNumber?: string | null;
  PoleId: number | null;
  PoleName: string | null;
  CreatedAt: string | null;
  EndDate: string | null;
}

export interface StatusDetail {
  vigente: number;
  permanente: number;
  isento: number;
  vigenteEmRenovacao: number;
}

export interface UnitsKpisResponse {
  totalUnidades: number;
  alvara: {
    regular: number;
    em_andamento: number;
    irregular: number;
    detail: StatusDetail;
  };
  avcb: {
    regular: number;
    em_andamento: number;
    irregular: number;
    detail: StatusDetail;
  };
}

export interface UnitsByUFResponse {
  uf: string;
  total: number;
  regular: number;
  emAndamento: number;
  irregular: number;
  regularConjunto?: number;
  naoRegularConjunto?: number;
  alvaraRegular?: number;
  alvaraNaoRegular?: number;
  avcbRegular?: number;
  avcbNaoRegular?: number;
  nxtTotal?: number;
  nxtRegularConjunto?: number;
  nxtNaoRegularConjunto?: number;
}

export interface NovasBaixadasResponse {
  month: string;
  novas: number;
  baixadas: number;
  total: number;
}

export interface UnitsWithCnpjKpisResponse {
  totalUnidades: number;
  regular: number;
  em_andamento: number;
  irregular: number;
  regularDetail: {
    vigente: number;
    permanente: number;
    isento: number;
    vigenteRenovacao: number;
  };
}

export interface RegionalData {
  regional: string;
  total: number;
}

async function fetchJson<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }
  
  const response = await fetch(url.toString());
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  const json = await response.json();
  // A API retorna { status, result, ... } - extrair result se existir
  return json.result ?? json;
}

/**
 * Hook para buscar KPIs de unidades
 */
export function useUnitsKpis() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'kpis', params],
    queryFn: () => fetchJson<UnitsKpisResponse>('/units/kpis', params),
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30, // 30 minutos
  });
}

/**
 * Hook para buscar unidades por UF (para o mapa)
 * A API agora retorna array com breakdown: [{ uf, total, regular, emAndamento, irregular }]
 */
export function useUnitsByUF() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'by-uf', params],
    queryFn: async () => {
      const data = await fetchJson<UnitsByUFResponse[]>('/units/by-uf', params);
      
      // API já retorna no formato correto, apenas garante que é array
      return Array.isArray(data) ? data : [];
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar dados de novas e baixadas (últimos 12 meses)
 */
export function useNovasBaixadas() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'novas-baixadas', params],
    queryFn: () => fetchJson<NovasBaixadasResponse[]>('/units/novas-baixadas', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar KPIs de unidades que possuem CNPJ no campo Complement
 * Considera apenas documentos AVCB/Alvará com CNPJ válido
 */
export function useUnitsWithCnpjKpis() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'kpis-cnpj', params],
    queryFn: () => fetchJson<UnitsWithCnpjKpisResponse>('/units/kpis-cnpj', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Response do endpoint de documentos com CNPJ
 */
export interface DocsWithCnpjKpisResponse {
  totalDocumentos: number;
  regular: number;
  em_andamento: number;
  irregular: number;
  regularDetail: {
    vigente: number;
    permanente: number;
    isento: number;
    vigenteRenovacao: number;
  };
}

/**
 * Hook para buscar KPIs de DOCUMENTOS (não unidades) com CNPJ
 * Conta cada documento Alvará de Funcionamento individualmente
 */
export function useDocsWithCnpjKpis() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'kpis-cnpj-docs', params],
    queryFn: () => fetchJson<DocsWithCnpjKpisResponse>('/units/kpis-cnpj-docs', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Response do endpoint de regularidade conjunta AVCB + Alvara
 */
export interface JointRegularityResponse {
  recorte: string;
  total: number;
  regularConjunto: number;
  naoRegularConjunto: number;
}

/**
 * Hook para buscar a regularidade conjunta AVCB + Alvara (recorte consolidado)
 * Uma unidade so e regular quando AVCB e Alvara estao regulares simultaneamente.
 */
export function useJointRegularity() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['documents', 'regularidade-conjunta', params],
    queryFn: () => fetchJson<JointRegularityResponse>('/documents/regularidade-conjunta', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar lista de regionais
 * A API retorna array de strings ['BA/SE', 'CO', ...]
 */
export function useRegionals() {
  return useQuery({
    queryKey: ['units', 'regionals'],
    queryFn: async () => {
      const data = await fetchJson<string[]>('/units/regionals');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 1000 * 60 * 30, // 30 minutos - muda raramente
    gcTime: 1000 * 60 * 60,
  });
}

/**
 * Hook para buscar lista de unidades filtradas
 */
export function useUnits() {
  const getFilterParams = useUnidadesFilters((state) => state.getFilterParams);
  const params = getFilterParams();

  return useQuery({
    queryKey: ['units', 'list', params],
    queryFn: async () => {
      // O endpoint de lista devolve { data, recordsTotal, recordsFiltered },
      // enquanto os demais endpoints retornam o array diretamente.
      const result = await fetchJson<Unit[] | { data?: Unit[] }>('/units', params);
      return Array.isArray(result) ? result : (result.data ?? []);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}
